import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Brand.css";
import "../styles/global.css";

/* ────────── Scroll Lock Fix Helper ────────── */
const unlockBodyScroll = () => {
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
};

/* ────────── Custom Pagination ────────── */
const ChevronLeftIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="br-pagination">
      <div className="br-pagination__info">
        <span>
          Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="br-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="br-pagination__pages">
        <button
          className="br-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="br-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`br-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="br-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

/* ────────── iOS Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm / Alert Dialog ────────── */
const ConfirmDialog = ({
  open, title, message, confirmLabel = "Confirm",
  confirmVariant = "danger", onConfirm, onCancel, alertOnly = false,
}) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : confirmVariant === "success" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          {!alertOnly && (
            <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── SVG Icons ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const SearchIcon = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const TagIcon = (p) => <Icon {...p} d={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>} />;
const CloseIcon = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;

/* ────────── Component ────────── */
const Brand = () => {
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filterText, setFilterText] = useState("");

  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  // Bulk-selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [switchingSlug, setSwitchingSlug] = useState(null);

  // Pagination state (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null, alertOnly: false,
  });

  const token = localStorage.getItem("authToken");

  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm, alertOnly = false }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm, alertOnly });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Attach hidden.bs.modal listener ── */
  useEffect(() => {
    const modalEl = document.getElementById("addBrandModal");
    if (!modalEl) return;
    const handleModalHidden = () => unlockBodyScroll();
    modalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
  }, []);

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Brands ── */
  const fetchBrands = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/brands`, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBrands(data.data);
          setFilteredBrands(data.data);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching brands:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBrands(); }, []);

  /* ── Search filter ── */
  useEffect(() => {
    const result = brands.filter((b) =>
      b.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      b.slug?.toLowerCase().includes(filterText.toLowerCase()) ||
      b.status?.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredBrands(result);
    setCurrentPage(1);
  }, [filterText, brands]);

  /* ── Paginated data (client-side) ── */
  const totalRows = filteredBrands.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const paginatedBrands = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredBrands.slice(start, start + perPage);
  }, [filteredBrands, currentPage, perPage]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerPageChange = (newPerPage) => { setPerPage(newPerPage); setCurrentPage(1); };

  /* ── Status toggle ── */
  const handleStatusChange = (slug, currentStatus) => {
    if (switchingSlug === slug) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this brand to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingSlug(slug);
        fetch(`${API_BASE_URL}api/brands/status/${slug}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus} successfully!`, "success");
              fetchBrands();
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error while updating status.", "danger"))
          .finally(() => setSwitchingSlug(null));
      },
    });
  };

  /* ── Close modal helper ── */
  const closeModal = useCallback(() => {
    const modalEl = document.getElementById("addBrandModal");
    if (!modalEl) return;
    const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
    if (modalInstance) {
      modalInstance.hide();
    } else {
      unlockBodyScroll();
    }
  }, []);

  /* ── Add or Update Brand ── */
  const handleSaveBrand = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/brands/${editSlug}`
      : `${API_BASE_URL}api/brands`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          flashMessage(isEditMode ? "Brand updated successfully!" : "Brand added successfully!", "success");
          setName("");
          setStatus("Active");
          setImage(null);
          setPreview(null);
          setIsEditMode(false);
          fetchBrands();
          closeModal();
        } else {
          flashMessage("Failed to save brand.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setStatus(row.status);
    setPreview(
      row.image
        ? `${API_BASE_URL}public/brand/${row.image}`
        : `${API_BASE_URL}public/noimage.jpg`
    );
    setImage(null);
    const modalEl = document.getElementById("addBrandModal");
    new window.bootstrap.Modal(modalEl).show();
  };

  /* ── Delete single ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Brand",
      message: "Are you sure you want to delete this brand? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/brands/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Brand deleted successfully!", "success");
              fetchBrands();
            } else {
              flashMessage("Failed to delete brand.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting brand.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected brand${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: async () => {
        closeConfirm();
        setBulkDeleting(true);
        try {
          const results = await Promise.all(
            selectedRows.map((row) =>
              fetch(`${API_BASE_URL}api/brands/destroy/${row.slug}`, {
                method: "GET",
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
              })
                .then((res) => res.json())
                .catch(() => ({ status: false }))
            )
          );
          const successCount = results.filter((r) => r.status).length;
          const failCount = count - successCount;
          if (failCount === 0) {
            flashMessage(`${successCount} brand${successCount > 1 ? "s" : ""} deleted successfully!`, "success");
          } else {
            flashMessage(`${successCount} deleted, ${failCount} failed.`, failCount === count ? "danger" : "warning");
          }
          setSelectedRows([]);
          setToggleCleared((p) => !p);
          fetchBrands();
        } catch {
          flashMessage("Error during bulk delete.", "danger");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  /* ── Open Add modal (reset state) ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setName("");
    setStatus("Active");
    setImage(null);
    setPreview(null);
  };

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => <span className="br-id-pill">#{row.id}</span>,
        width: "80px",
        sortable: true,
        selector: (row) => row.id,
      },
      {
        name: "Image",
        cell: (row) => (
          <div className="br-img-wrap">
            <img
              src={row.image ? `${API_BASE_URL}public/brand/${row.image}` : `${API_BASE_URL}public/noimage.jpg`}
              alt={row.name}
            />
          </div>
        ),
        width: "90px",
      },
      {
        name: "Name",
        selector: (row) => row.name,
        cell: (row) => <span className="br-name-cell">{row.name}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Slug",
        selector: (row) => row.slug,
        cell: (row) => <span className="br-slug-cell">{row.slug}</span>,
      },
      {
        name: "Status",
        cell: (row) => (
          <div className="br-status-wrap">
            <IosSwitch
              checked={row.status === "Active"}
              onChange={() => handleStatusChange(row.slug, row.status)}
              disabled={switchingSlug === row.slug}
            />
            <span className={`br-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
              {row.status === "Active" ? "Active" : "Inactive"}
            </span>
          </div>
        ),
        width: "150px",
      },
      {
        name: "Created At",
        selector: (row) => new Date(row.created_at).toLocaleDateString("en-IN"),
        cell: (row) => (
          <span className="br-date-cell">
            {new Date(row.created_at).toLocaleDateString("en-IN")}
          </span>
        ),
        sortable: true,
        width: "130px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="br-actions">
            <button
              className="br-action-btn br-edit"
              onClick={() => handleEdit(row)}
              title="Edit Brand"
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="br-action-btn br-del"
              onClick={() => handleDelete(row.slug)}
              title="Delete Brand"
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        ),
        width: "110px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [switchingSlug]
  );

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {(loading || bulkDeleting) && (
          <div className="br-overlay">
            <div className="br-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="brand-shell">

              {/* ── Page header ── */}
              <div className="brand-head">
                <div>
                  <h2>Brands</h2>
                  <p>Manage your product brands — add, edit, or organize them.</p>
                </div>
                <button
                  type="button"
                  className="add-btn"
                  data-bs-toggle="modal"
                  data-bs-target="#addBrandModal"
                  onClick={openAddModal}
                >
                  <PlusIcon size={18} />
                  Add Brand
                </button>
              </div>

              {/* ── Card ── */}
              <div className="brand-card">
                <div className="card-top">
                  <div className="search-box">
                    <SearchIcon size={18} />
                    <input
                      type="text"
                      placeholder="Search by name, slug or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="total-badge">
                    <TagIcon size={14} />
                    Total:&nbsp;<b>{filteredBrands.length}</b>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`br-alert ${messageType}`} role="alert">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {messagealert}
                    <button type="button" className="close-btn" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {/* Bulk action bar */}
                {selectedRows.length > 0 && (
                  <div className="bulk-bar">
                    <div className="bulk-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                        <polyline points="9 11 12 14 22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      <strong>{selectedRows.length}</strong>
                      &nbsp;item{selectedRows.length === 1 ? "" : "s"} selected
                    </div>
                    <div className="bulk-actions">
                      <button
                        type="button"
                        className="bulk-clear"
                        onClick={() => { setSelectedRows([]); setToggleCleared((p) => !p); }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="bulk-delete-btn"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                        {bulkDeleting ? "Deleting…" : "Delete Selected"}
                      </button>
                    </div>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={paginatedBrands}
                  customStyles={tableStyles}
                  highlightOnHover
                  striped
                  responsive
                  selectableRows
                  onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                  clearSelectedRows={toggleCleared}
                  pagination={false}
                  noDataComponent={
                    <div style={{ padding: "56px 24px", textAlign: "center", color: "var(--br-muted)" }}>
                      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#c7d3ce", display: "block", margin: "0 auto 12px" }}>
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                        <line x1="7" y1="7" x2="7.01" y2="7" />
                      </svg>
                      <p style={{ fontWeight: 600, fontSize: 15, color: "#334155", margin: "0 0 4px" }}>No brands found</p>
                      <p style={{ fontSize: 13.5, margin: 0 }}>Click "Add Brand" to create your first one.</p>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          <div
            className="modal fade"
            id="addBrandModal"
            tabIndex="-1"
            aria-labelledby="addBrandModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <form id="addBrandForm" onSubmit={handleSaveBrand}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="addBrandModalLabel">
                      {isEditMode ? "Edit Brand" : "Add New Brand"}
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label htmlFor="brandName" className="form-label">Brand Name</label>
                        <input
                          type="text" className="form-control" id="brandName"
                          placeholder="Enter brand name" value={name}
                          onChange={(e) => setName(e.target.value)} required
                        />
                      </div>
                      <div className="col-12">
                        <label htmlFor="brandImage" className="form-label">Image</label>
                        <input
                          type="file" className="form-control" id="brandImage"
                          accept="image/*" onChange={(e) => setImage(e.target.files[0])}
                        />
                        {preview && (
                          <img src={preview} alt="Preview" className="preview-thumb" />
                        )}
                      </div>
                      <div className="col-12 col-sm-6">
                        <label htmlFor="brandStatus" className="form-label">Status</label>
                        <select
                          className="form-select" id="brandStatus"
                          value={status} onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                    <button type="submit" className="btn btn-primary">
                      {isEditMode ? "Update Brand" : "Add Brand"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Custom Confirm Dialog */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
            alertOnly={confirmDialog.alertOnly}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Brand;